import { useEffect, useState } from "react";
import { Sparkles, Languages, Wallet, BarChart3, ArrowRight, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import AddAddonDialog from "./AddAddonDialog";
import WaitlistDialog from "./WaitlistDialog";

const ICON_MAP = {
  languages: Languages,
  wallet: Wallet,
  chart: BarChart3,
  sparkles: Sparkles,
};

export default function RecommendedAddon() {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    api.get("/billing/recommendation")
      .then((r) => setRec(r.data.recommendation))
      .catch(() => setRec(null))
      .finally(() => setLoading(false));
  }, []);

  async function dismiss() {
    setDismissing(true);
    try {
      await api.post("/billing/recommendation/dismiss");
      setRec(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not dismiss");
    } finally {
      setDismissing(false);
    }
  }

  if (loading || !rec) return null;

  const Icon = ICON_MAP[rec.icon] || Sparkles;
  const isComingSoon = !!rec.coming_soon;

  return (
    <>
      <div
        data-testid="recommended-addon-card"
        className="bg-gradient-to-br from-[#F0EDE8] to-white border border-[#2E5C50]/20 rounded-xl p-5 relative overflow-hidden"
      >
        <button
          data-testid="recommended-addon-dismiss"
          onClick={dismiss}
          disabled={dismissing}
          aria-label="Dismiss"
          className="absolute top-3 right-3 text-[#57605A] hover:text-[#1C211F] transition-colors disabled:opacity-40"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-11 h-11 bg-[#D6E0D9] rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#C07A60]">
              ✨ Recommended for you
            </p>
            <h3 className="mt-1 font-heading font-semibold text-base text-[#1C211F]">
              {rec.headline || rec.addon_name}
            </h3>
            <p className="mt-1.5 text-sm text-[#57605A] leading-snug">{rec.reason}</p>

            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                data-testid="recommended-addon-cta"
                onClick={() => (isComingSoon ? setShowWaitlist(true) : setShowAdd(true))}
                disabled={dismissing}
                className="flex items-center gap-1.5 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-4 py-2 text-sm font-medium"
              >
                {dismissing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isComingSoon ? rec.cta || "Join waitlist" : `Add for $${rec.amount}/mo`}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid="recommended-addon-snooze"
                onClick={dismiss}
                disabled={dismissing}
                className="text-xs font-medium text-[#57605A] hover:text-[#1C211F]"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddAddonDialog
        addon={{
          id: rec.addon_id,
          name: rec.addon_name,
          amount: rec.amount,
          description: rec.headline || "",
        }}
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onAdded={() => { setRec(null); setShowAdd(false); }}
      />

      <WaitlistDialog
        addon={isComingSoon ? {
          id: rec.addon_id,
          name: rec.addon_name,
          amount: rec.amount,
          description: rec.headline || "",
        } : null}
        onClose={() => setShowWaitlist(false)}
      />
    </>
  );
}
